import type { CompileContext, EntityInstance, MapDocument, WorldDocument } from "daedalus-core";
import { spawnAreaCells } from "../creatures/spawn.ts";
import { readNpcFields } from "../npcs/marker.ts";
import { AUTHORING_CELL, SIXTEENTHS, canStand, type CollisionGrid } from "../movement/step.ts";
import { compiledGridOf } from "./map-grid.ts";
import { NPC } from "./type-names.ts";

/**
 * The world-dependent checks of the `npc` marker (ADR-136 decision 2.1), run where a document is compiled and never on
 * an edit, since they resolve the map's terrain: the marker's own feet must be able to stand, an `area` needs at least
 * one cell to stand on, and every point of a `path` must be standable. A marker whose fields fail `readNpcFields` is left
 * to the field checks that already name it.
 */

/**
 * Whether the feet box can stand at a point in tiles.
 *
 * @param grid The map's compiled grid.
 * @param x The feet x in tiles.
 * @param y The feet y in tiles.
 * @returns True when nothing at the point blocks.
 */
function standsAt(grid: CollisionGrid, x: number, y: number): boolean {
  return canStand(Math.round(x * SIXTEENTHS), Math.round(y * SIXTEENTHS), grid);
}

/**
 * Whether the feet box can stand on a cell of the authoring grid, at the cell's bottom centre as the map server places a
 * creature or an NPC there.
 *
 * @param grid The map's compiled grid.
 * @param cell The cell as `[column, row]`.
 * @returns True when nothing there blocks.
 */
function standsOnCell(grid: CollisionGrid, cell: readonly [number, number]): boolean {
  return canStand(cell[0] * AUTHORING_CELL + AUTHORING_CELL / 2, (cell[1] + 1) * AUTHORING_CELL, grid);
}

/**
 * The Issues of one NPC marker.
 *
 * @param map The marker's map.
 * @param marker The marker.
 * @param grid The map's compiled grid.
 * @returns One sentence per problem, empty when the marker's fields do not read or all is well.
 */
function markerIssues(map: MapDocument, marker: EntityInstance, grid: CollisionGrid): string[] {
  const verdict = readNpcFields(marker.fields);
  if (!verdict.ok) {
    return [];
  }
  const { movement, area, path } = verdict.npc;
  const called = `npc ${marker.id} on map ${map.id}`;
  const issues: string[] = [];
  if (!standsAt(grid, marker.x, marker.y)) {
    issues.push(`${called} stands where nothing can stand, at (${marker.x}, ${marker.y}); move it onto open ground`);
  }
  if (movement === "area" && !spawnAreaCells(marker.x, marker.y, area).some((cell) => standsOnCell(grid, cell))) {
    issues.push(`${called} moves within an area with no cell to stand on; paint at least one cell on open ground`);
  }
  if (movement === "path") {
    const cells = spawnAreaCells(marker.x, marker.y, path);
    const bad = cells.findIndex((cell) => !standsOnCell(grid, cell));
    if (bad >= 0) {
      const at = path[bad] as readonly [number, number];
      issues.push(`${called} walks a path whose point ${bad + 1} of ${path.length}, at cell (${at[0]}, ${at[1]}), cannot be stood on; move the point onto open ground`);
    }
  }
  return issues;
}

/**
 * The standing problems of every `npc` marker of a document.
 *
 * @param world The document, upgraded and valid, as a compile holds it.
 * @param context The compile context.
 * @returns One sentence per problem, in document order, each naming the marker and its map; empty when the document
 *   places no NPC or all can stand.
 * @throws Error when a map holding an NPC does not resolve, as `compiledGridOf` throws.
 */
export function npcStandingIssues(world: WorldDocument, context: CompileContext): string[] {
  return world.maps.flatMap((map) => {
    const markers = map.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities.filter((entity) => entity.type === NPC) : []));
    if (markers.length === 0) {
      return [];
    }
    const grid = compiledGridOf(world, map, context);
    return markers.flatMap((marker) => markerIssues(map, marker, grid));
  });
}
